import { Cameraswitch, Close, PhotoCamera } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { createWorker } from "tesseract.js";
import { detectReceiptBlocks, ReceiptBlock } from "../utils/detectReceiptRows";

interface ScanReceiptsCameraProps {
  open: boolean;
  onClose: () => void;
  onCapture: (files: File[]) => void;
}

export const ScanReceiptsCamera: FC<ScanReceiptsCameraProps> = ({
  open,
  onClose,
  onCapture,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const workerRef = useRef<any>(null);
  const detectingRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blocks, setBlocks] = useState<ReceiptBlock[]>([]);
  const [facingMode, setFacingMode] = useState<"environment" | "user">(
    "environment"
  );

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const drawBlocks = useCallback((nextBlocks: ReceiptBlock[]) => {
    const video = videoRef.current;
    const canvas = overlayRef.current;
    if (!video || !canvas) return;
    const width = video.clientWidth || video.videoWidth;
    const height = video.clientHeight || video.videoHeight;
    if (!width || !height || !video.videoWidth) return;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, width, height);
    const scaleX = width / video.videoWidth;
    const scaleY = height / video.videoHeight;
    nextBlocks.forEach((block) => {
      ctx.strokeStyle = block.needs_review ? "#ed6c02" : "#2e7d32";
      ctx.lineWidth = 3;
      ctx.strokeRect(
        block.box.x * scaleX,
        block.box.y * scaleY,
        block.box.width * scaleX,
        Math.max(36 * scaleY, block.box.height * scaleY)
      );
      const label = block.needs_review
        ? "Re-verify receipt"
        : `Receipt ${block.receipt_no}`;
      ctx.fillStyle = block.needs_review ? "#ed6c02" : "#2e7d32";
      ctx.font = "14px sans-serif";
      ctx.fillText(
        label,
        block.box.x * scaleX + 8,
        Math.max(16, block.box.y * scaleY + 18)
      );
    });
  }, []);

  const startCamera = useCallback(async () => {
    stopStream();
    setError(null);
    try {
      // Ask for the highest resolution the camera will give. A full ETAT page
      // holds ~20 ticket rows, so at 720p the 9-digit ticket number is only a
      // few dozen pixels wide and the model is reading mush. These are `ideal`
      // hints, so a weaker camera still works, just at its own maximum.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: 3840 },
          height: { ideal: 2160 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err: any) {
      setError(err?.message || "Camera permission denied");
    }
  }, [facingMode, stopStream]);

  useEffect(() => {
    if (!open) {
      stopStream();
      setReady(false);
      setBlocks([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        if (!workerRef.current) {
          workerRef.current = await createWorker("eng");
        }
        if (!cancelled) setReady(true);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || "Failed to start OCR");
      }
    })();
    startCamera();
    return () => {
      cancelled = true;
      stopStream();
    };
  }, [open, startCamera, stopStream]);

  useEffect(() => {
    if (!open || !ready) return;
    const timer = window.setInterval(async () => {
      const video = videoRef.current;
      const worker = workerRef.current;
      if (!video || !worker || detectingRef.current || video.readyState < 2) {
        return;
      }
      detectingRef.current = true;
      try {
        const frame = document.createElement("canvas");
        const maxWidth = 960;
        const scale = Math.min(1, maxWidth / video.videoWidth);
        frame.width = Math.max(1, Math.round(video.videoWidth * scale));
        frame.height = Math.max(1, Math.round(video.videoHeight * scale));
        const ctx = frame.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(video, 0, 0, frame.width, frame.height);
        const result = await worker.recognize(frame);
        const nextBlocks = detectReceiptBlocks(
          (result.data.words || []).map((word: any) => ({
            text: word.text,
            confidence: word.confidence,
            bbox: word.bbox,
          })),
          frame.width,
          frame.height
        ).map((block) => ({
          ...block,
          box: {
            x: block.box.x / scale,
            y: block.box.y / scale,
            width: block.box.width / scale,
            height: block.box.height / scale,
          },
        }));
        setBlocks(nextBlocks);
        drawBlocks(nextBlocks);
      } catch (err) {
        console.warn("Live receipt detection failed", err);
      } finally {
        detectingRef.current = false;
      }
    }, 1800);
    return () => window.clearInterval(timer);
  }, [open, ready, drawBlocks]);

  const handleCapture = async () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    // Quality 0.95: the still is re-encoded once more server-side before it
    // reaches the model, and JPEG artefacts around small digits compound.
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `scan_${Date.now()}.jpg`, {
        type: "image/jpeg",
      });
      onCapture([file]);
      onClose();
    }, "image/jpeg", 0.95);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        Scan receipts
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Box
            sx={{
              position: "relative",
              bgcolor: "#111",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              style={{ width: "100%", display: "block" }}
            />
            <canvas
              ref={overlayRef}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
              }}
            />
            {!ready && (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  position: "absolute",
                  inset: 0,
                  bgcolor: "rgba(0,0,0,0.45)",
                }}
              >
                <CircularProgress />
              </Stack>
            )}
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          Green box = one full receipt ticket. Orange = receipt number is unclear
          and will stay empty for re-verify. {blocks.length} ticket(s) detected.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<Cameraswitch />}
          onClick={() =>
            setFacingMode((mode) =>
              mode === "environment" ? "user" : "environment"
            )
          }
        >
          Flip camera
        </Button>
        <Button
          variant="contained"
          startIcon={<PhotoCamera />}
          disabled={!ready || Boolean(error)}
          onClick={handleCapture}
        >
          Capture & parse
        </Button>
      </DialogActions>
    </Dialog>
  );
};
